// 💸 THE LEASE'S MONEY, ASKED THE WAY A PERSON THINKS ABOUT IT (owner 2026-09-17: "This flow is
// really ambiguous… Be human being for a moment.")
//
// The v8 lease stores two payment stages as ~20 separate fields (recipient type, method,
// completion rule, processing days, bank charges, bank, holder, IBAN, SWIFT… twice). The form
// asked them one by one, scattered over three groups, in an order where the account picker came
// BEFORE the question of who receives the money — and it filled in "monthly rent: local bank
// transfer" on its own, so an agent who said "cash" was stopped by an owner IBAN nobody mentioned.
//
// A person answers three questions:
//   A. what the tenant pays at signing            (the prepayment list, deposit, earlier payments)
//   B. who takes that money, and how              → SIGNING_ROUTES
//   C. how monthly rent is paid afterwards        → MONTHLY_ROUTES ("the same way" by default)
// Everything else — completion rule, processing days — follows from the answer. The STORED FIELDS
// ARE UNCHANGED: a route is a way of writing them, so the server rules and the printed contract
// (lib/rental_payment_rules.js, contract_render.js) do not move.
//
// ⚠️ Steps, not a patch: the composer's setValue already carries per-key consequences (choosing
//    the owner as recipient picks the owner's saved account; choosing a method derives its
//    completion rule). Returning ordered [key, value] steps reuses those, instead of a second
//    copy of them that would drift.

export const TRANSFER_KINDS = ['local_bank_transfer', 'instant_transfer', 'international_wire']

/**
 * What a payment METHOD implies for its stage — the approved table, in ONE place (the composer's
 * setValue applies it; tests read it). Expected/normal processing, never a promise of arrival:
 *   cash → on cash receipt, 0 · instant → on funds received, 0 · local → on funds received, 1
 *   SWIFT → at signing "on verified initiation", monthly "on funds received"; up to 3 banking days
 *   any / multiple (monthly only) → per the way actually used
 * ⚠️ "Other" is not here: its completion rule is the custom description the agent writes.
 */
export function methodDerivations(stage, method) {
  const completion = method === 'cash' ? 'on_cash_receipt'
    : method === 'any' || method === 'multiple' ? 'per_method'
    : method === 'international_wire' && stage === 'initial' ? 'on_verified_initiation' : 'on_funds_received'
  const days = method === 'international_wire' ? 3 : method === 'local_bank_transfer' ? 1 : 0
  return { [`${stage}_completion_rule`]: completion, [`${stage}_processing_days`]: days }
}
const isTransfer = m => TRANSFER_KINDS.includes(m)

export const SIGNING_ROUTES = [
  { id: 'owner_cash', recipient: 'owner', method: 'cash' },
  { id: 'agent_cash', recipient: 'authorised_agent', method: 'cash' },
  { id: 'owner_transfer', recipient: 'owner', method: 'transfer' },
  { id: 'agent_transfer', recipient: 'authorised_agent', method: 'transfer' },
  { id: 'agency_transfer', recipient: 'agency_settlement_account', method: 'transfer' },
  { id: 'owner_card', recipient: 'owner', method: 'card_or_electronic_payment' },
  { id: 'other', recipient: 'other', method: 'other' },
]
// 'any' / 'multiple' (owner 2026-09-17: "not defined — any type can be used anytime that's convenient",
// "you can also make multi selections"): the monthly payment counts by the way actually used.
export const MONTHLY_ROUTES = ['same', 'any', 'multiple', 'owner_cash', 'owner_transfer', 'owner_card', 'other']
export const MONTHLY_WAYS = ['cash', 'local_bank_transfer', 'instant_transfer', 'international_wire', 'card_or_electronic_payment']

/** Which signing route the stored values describe. */
export function signingRouteOf(v) {
  const r = v?.initial_recipient_type || 'owner', m = v?.payment_method
  if (r === 'other' || m === 'other') return 'other'
  if (r === 'agency_settlement_account') return 'agency_transfer'
  if (r === 'authorised_agent') return isTransfer(m) ? 'agent_transfer' : 'agent_cash'
  if (isTransfer(m)) return 'owner_transfer'
  if (m === 'card_or_electronic_payment') return 'owner_card'
  return 'owner_cash'
}

/** A remote signing cannot be paid in cash — the server's rule (lib/remote_rental.js), and the only one.
 *  (2026-09-19: it used to offer transfers only, and forced SWIFT; the method is the opening answer now.) */
export const signingRouteAllowed = (id, remote) => !remote || !['owner_cash', 'agent_cash'].includes(id)

/** Ordered [key, value] writes that turn the values into `id`. */
export function signingSteps(id, v, { remote = false } = {}) {
  const route = SIGNING_ROUTES.find(r => r.id === id)
  if (!route) return []
  const steps = []
  if ((v?.initial_recipient_type || null) !== route.recipient) steps.push(['initial_recipient_type', route.recipient])
  // ⚠️ The kind of transfer is the opening answer — a remote signing does NOT imply SWIFT (2026-09-19)
  const method = route.method === 'transfer'
    ? (isTransfer(v?.payment_method) ? v.payment_method : 'local_bank_transfer')
    : route.method
  // setValue derives the completion rule and processing days from the method — always re-apply it
  steps.push(['payment_method', method])
  if (route.method === 'other') steps.push(['initial_completion_rule', 'custom'])
  if (route.recipient === 'agency_settlement_account' && !String(v?.payment_recipient || '').trim()) steps.push(['payment_recipient', 'senTavus'])
  if (!v?.initial_bank_charges) steps.push(['initial_bank_charges', 'tenant'])
  return steps
}

/** 💸 WHEN BANK-FEE TERMS APPLY (contract reviews 2026-10-09, apartment lease finding 2): a stage's fee choice and its
 *  shared-fee note are terms only where a TRANSFER is allowed — the initial payment by transfer; monthly rent by a
 *  transfer, by "any way", or by "several ways" with a transfer ticked. Mirrors server/lib/rental_payment_rules.js. */
export function transfersAllowed(stage, v) {
  if (stage === 'initial') return TRANSFER_KINDS.includes(v?.payment_method)
  const m = v?.recurring_payment_method
  return TRANSFER_KINDS.includes(m) || m === 'any'
    || (m === 'multiple' && (Array.isArray(v?.recurring_payment_methods) ? v.recurring_payment_methods : []).some(x => TRANSFER_KINDS.includes(x)))
}
/** …and where none is allowed, a "shared fees" answer left from an earlier choice is reset, with its note: an inactive
 *  term must neither block saving (its note was hidden) nor survive into the document. */
export function inactiveFeeSteps(v) {
  const steps = []
  for (const stage of ['initial', 'recurring']) {
    if (transfersAllowed(stage, v) || v?.[`${stage}_bank_charges`] !== 'shared') continue
    steps.push([`${stage}_bank_charges`, 'tenant'])
    if (String(v?.[`${stage}_bank_charges_note`] || '').trim()) steps.push([`${stage}_bank_charges_note`, ''])
    // …and its English/Russian text with it (core 3144), so no translation of a withdrawn note is left behind
    for (const l of ['en', 'ru']) if (String(v?.[`${stage}_bank_charges_note_${l}`] || '').trim()) steps.push([`${stage}_bank_charges_note_${l}`, ''])
  }
  return steps
}

/** "Same" means the same METHOD; recurring rent still has its own owner recipient/account. */
export const monthlySameAvailable = v => ['cash', 'card_or_electronic_payment', ...TRANSFER_KINDS].includes(v?.payment_method)

export function monthlyRouteOf(v, { preferSame = true } = {}) {
  const m = v?.recurring_payment_method
  if (m === 'other') return 'other'
  if (m === 'any' || m === 'multiple') return m
  const same = monthlySameAvailable(v) && m === v.payment_method
  if (same && preferSame) return 'same'
  if (isTransfer(m)) return 'owner_transfer'
  if (m === 'card_or_electronic_payment') return 'owner_card'
  if (m === 'cash') return 'owner_cash'
  return monthlySameAvailable(v) ? 'same' : 'owner_transfer'
}
const compact = s => String(s || '').replace(/\s+/g, '').toUpperCase()

export function monthlySteps(id, v) {
  const steps = [['recurring_recipient_type', 'owner']]
  if (id === 'same') {
    steps.push(['recurring_payment_method', v.payment_method])
    // setValue derives the recurring completion rule and normal processing time from this method.
    // Never copy the initial recipient's account: monthly rent has its own owner account.
    if (!v?.recurring_bank_charges) steps.push(['recurring_bank_charges', 'tenant'])
    return steps
  }
  if (id === 'any' || id === 'multiple') {
    // setValue derives completion rule "per_method" from the method
    steps.push(['recurring_payment_method', id])
    if (!v?.recurring_bank_charges) steps.push(['recurring_bank_charges', 'tenant'])
    return steps
  }
  const method = id === 'owner_cash' ? 'cash' : id === 'owner_card' ? 'card_or_electronic_payment' : id === 'other' ? 'other'
    : isTransfer(v?.recurring_payment_method) ? v.recurring_payment_method : 'local_bank_transfer'
  steps.push(['recurring_payment_method', method])
  if (id === 'other') steps.push(['recurring_completion_rule', 'custom'])
  if (!v?.recurring_bank_charges) steps.push(['recurring_bank_charges', 'tenant'])
  return steps
}

// ── what is still wrong, said as a person would say it ──────────────────────────────────────
// Every message names the thing to do. The old bar said „მესაკუთრის IBAN — გადახდის წესი
// შეამოწმეთ", which an agent who chose cash could not act on.
const MSG = {
  signing_route: { ka: 'აირჩიეთ, ვინ იღებს ხელმოწერისას გადასახდელ თანხას და როგორ', en: 'Choose who takes the payment at signing, and how' },
  initial_recipient_name: { ka: 'ჩაწერეთ, ვინ იღებს ხელმოწერისას გადასახდელ თანხას', en: 'Name who receives the payment at signing' },
  initial_other: { ka: 'აღწერეთ, როგორ და როდის გადაიხდება ხელმოწერისას გადასახდელი თანხა', en: 'Describe how and when the payment at signing is made' },
  initial_receiving_agent: { ka: 'აირჩიეთ აგენტი, რომელიც თანხას იღებს', en: 'Choose the agent who receives the money' },
  initial_account: { ka: 'აირჩიეთ ან დაამატეთ ანგარიში, რომელზეც დამქირავებელი ხელმოწერისას თანხას ჩარიცხავს', en: 'Choose or add the account the tenant transfers the payment at signing to' },
  initial_swift: { ka: 'საერთაშორისო გადარიცხვისთვის მიუთითეთ მიმღების ბანკის SWIFT-კოდი', en: 'An international transfer needs the receiving bank\'s SWIFT code' },
  approved_agent_account: { ka: 'აგენტის ანგარიში ადმინისტრატორმა უნდა დაადასტუროს — ან აირჩიეთ ნაღდი ანგარიშსწორება', en: 'The agent\'s account must be approved by an administrator — or choose cash' },
  monthly_route: { ka: 'აირჩიეთ, როგორ გადაიხდება ყოველთვიური ქირა', en: 'Choose how monthly rent is paid' },
  monthly_ways: { ka: 'მონიშნეთ, რომელი გზებით შეიძლება ყოველთვიური ქირის გადახდა', en: 'Tick the ways monthly rent may be paid' },
  monthly_other: { ka: 'აღწერეთ, როგორ და როდის გადაიხდება ყოველთვიური ქირა', en: 'Describe how and when monthly rent is paid' },
  owner_account: { ka: 'აირჩიეთ ან დაამატეთ მესაკუთრის ანგარიში, რომელზეც ყოველთვიური ქირა ჩაირიცხდება', en: 'Choose or add the owner\'s account for monthly rent' },
  owner_swift: { ka: 'საერთაშორისო გადარიცხვისთვის მიუთითეთ მესაკუთრის ბანკის SWIFT-კოდი', en: 'An international transfer needs the owner\'s bank SWIFT code' },
  bank_charges_note: { ka: 'ჩაწერეთ, როგორ ნაწილდება საბანკო ხარჯები', en: 'Say how the bank charges are shared' },
  remote_route: { ka: 'დისტანციური გაფორმებისას ხელმოწერისას გადასახდელი თანხა უნდა ჩაირიცხოს ანგარიშზე', en: 'A remote signing needs the payment at signing to be a transfer' },
}
const FIELD_TO_MSG = {
  payment_account: 'initial_account', payment_bank: 'initial_account', initial_payment_swift: 'initial_swift',
  owner_payment_iban: 'owner_account', owner_payment_bank: 'owner_account', owner_payment_swift: 'owner_swift',
  initial_custom_completion_rule: 'initial_other', recurring_custom_completion_rule: 'monthly_other', recurring_payment_methods: 'monthly_ways',
  initial_completion_rule: 'initial_other', recurring_completion_rule: 'monthly_other',
  initial_receiving_agent: 'initial_receiving_agent', initial_recipient_name: 'initial_recipient_name',
  initial_bank_charges_note: 'bank_charges_note', recurring_bank_charges_note: 'bank_charges_note',
}
export const paymentMessage = (key, lang = 'ka') => {
  const m = MSG[key] || MSG[FIELD_TO_MSG[key]]
  return m ? (m[lang] || m.ka) : null
}

// ── a Georgian account against the bank registry (mirrors lib/payment_account.js + rental_payment_rules.js) ──
const ibanChecksum = iban => {
  let mod = 0
  for (const c of iban.slice(4) + iban.slice(0, 4)) {
    const digits = /[A-Z]/.test(c) ? String(c.charCodeAt(0) - 55) : c
    for (const d of digits) mod = (mod * 10 + Number(d)) % 97
  }
  return mod === 1
}
/**
 * 'iban' | 'bank' | 'swift' | null for a GE account. `banks` is the draft's `payment_banks`.
 * ⚠️ AN EMPTY REGISTRY IS NOT "NO REGISTRY". The server always hands paymentRulesProblem the array it
 *    fetched, and `[]` is truthy there — so with no active bank every GE account is refused as
 *    `unknown_georgian_bank_code`. The form skipped the check whenever the list was empty and said
 *    "ready". Only a draft that carries NO list (null/undefined — a template that is not the lease)
 *    skips the registry half, as the server does for a caller passing no banks.
 */
export function georgianAccountProblem(iban, bank, swift, banks) {
  const c = compact(iban)
  if (!/^GE\d{2}[A-Z]{2}[A-Z0-9]{16}$/.test(c) || !ibanChecksum(c)) return 'iban'
  if (!Array.isArray(banks)) return null
  const known = banks.find(b => b.iban_code === c.slice(4, 6) && b.active !== false)
  if (!known) return 'iban'
  const name = String(bank || '').trim()
  if (name !== known.name_ka && name !== known.name_en) return 'bank'
  if (String(swift || '').toUpperCase() !== known.swift) return 'swift'
  return null
}

// ── a remembered saved-account choice must still describe the values (core_contract.js accountSelectionProblem) ──
const STAGE_KEYS = {
  initial: ['payment_account', 'payment_bank', 'payment_account_holder', 'initial_payment_swift'],
  recurring: ['owner_payment_iban', 'owner_payment_bank', 'owner_payment_account_holder', 'owner_payment_swift'],
}
const accountMatches = (a, v, stage) => {
  const [iban, bank, holder, swift] = STAGE_KEYS[stage].map(k => v?.[k])
  return !!a && compact(iban) === a.iban && String(bank || '') === a.bank_name
    && String(holder || '') === a.account_holder && String(swift || '') === a.swift
}
/**
 * The account selection sent with the save, corrected against the values it would be sent with.
 * 🔴 "Monthly rent: the same way" COPIES the signing account into the owner's recurring fields. The
 *    selection still remembered the owner's earlier recurring account, so the save posted an id whose
 *    IBAN no longer matched and the server refused `recurring_account_selection` — for a choice the
 *    agent never made. A stage whose remembered account no longer matches is cleared (a hand-typed
 *    account needs no id); when the recurring fields now ARE the owner's signing account, that id is
 *    carried over. Returns the SAME object when nothing changes.
 */
export function reconcileAccountSelection(selection, values, accounts) {
  const list = Array.isArray(accounts) ? accounts : []
  let out = selection || null
  for (const stage of ['initial', 'recurring']) {
    const id = out?.[stage]
    if (!id) continue
    const a = list.find(x => x.id === id)
    if (a && !accountMatches(a, values, stage)) out = { ...out, [stage]: null }
  }
  const initial = out?.initial && list.find(x => x.id === out.initial)
  if (!out?.recurring && initial && (values?.initial_recipient_type || 'owner') === 'owner' && accountMatches(initial, values, 'recurring'))
    out = { ...(out || {}), recurring: initial.id }
  return out
}

/** The plan-level problems the field-by-field check cannot phrase — first one only, like the server. */
export function paymentPlanProblem(v) {
  const route = signingRouteOf(v)
  if (v?.remote_transaction === true && !signingRouteAllowed(route, true)) return 'remote_route'
  if (route === 'other' && !String(v?.payment_recipient || '').trim()) return 'initial_recipient_name'
  if (route === 'other' && !String(v?.initial_custom_completion_rule || '').trim()) return 'initial_other'
  if (v?.recurring_payment_method === 'other' && !String(v?.recurring_custom_completion_rule || '').trim()) return 'monthly_other'
  if (v?.recurring_payment_method === 'multiple' && !(Array.isArray(v?.recurring_payment_methods) && v.recurring_payment_methods.length)) return 'monthly_ways'
  return null
}

export function rentalPaymentProblem(v, banks = null) {
  const bank = new Set(['instant_transfer', 'local_bank_transfer', 'international_wire'])
  const single = new Set(['cash', ...bank, 'card_or_electronic_payment'])
  const allowed = new Set([...single, 'other', 'any', 'multiple'])
  for (const stage of ['initial', 'recurring']) {
    const method = stage === 'initial' ? v.payment_method : v.recurring_payment_method
    const rule = v[`${stage}_completion_rule`]
    const days = Number(v[`${stage}_processing_days`])
    if (!allowed.has(method)) return stage === 'initial' ? 'payment_method' : 'recurring_payment_method'
    // monthly rent "any convenient way" / "several ways" (owner 2026-09-17) — counted by the way used
    const flexible = method === 'any' || method === 'multiple'
    if (flexible && stage === 'initial') return 'payment_method'
    if (flexible !== (rule === 'per_method')) return `${stage}_completion_rule`
    const ways = method === 'multiple' ? (Array.isArray(v.recurring_payment_methods) ? v.recurring_payment_methods : []) : []
    if (method === 'multiple' && (!ways.length || ways.some(m => !single.has(m)))) return 'recurring_payment_methods'
    if (!['on_funds_received', 'on_verified_initiation', 'on_cash_receipt', 'custom', 'per_method'].includes(rule)
      || (method === 'cash') !== (rule === 'on_cash_receipt')) return `${stage}_completion_rule`
    if (method === 'other' && rule !== 'custom') return `${stage}_completion_rule`
    if (rule === 'custom' && !String(v[`${stage}_custom_completion_rule`] || '').trim()) return `${stage}_custom_completion_rule`
    if (!Number.isInteger(days) || days < 0 || (method === 'instant_transfer' && days !== 0)) return `${stage}_processing_days`
    if (!['tenant', 'owner', 'shared', 'recipient'].includes(v[`${stage}_bank_charges`])) return `${stage}_bank_charges`
    const iban = stage === 'initial' ? v.payment_account : v.owner_payment_iban
    const needsAccount = bank.has(method) || ways.some(m => bank.has(m)) || (method === 'any' && String(iban || '').trim() !== '')
    if (needsAccount && !/^[A-Z]{2}[A-Z0-9]{13,32}$/.test(String(iban || '').replace(/\s/g, '').toUpperCase()))
      return stage === 'initial' ? 'payment_account' : 'owner_payment_iban'
    // 🔴 THE BANK'S NAME, AND WITHOUT ASKING THE REGISTRY (2026-09-21). The server refuses a
    // bank-ish payment whose bank name is blank — lib/rental_payment_rules.js:
    //     if (bankish && (!String(p.bank||"").trim() || !ibanOk(p.iban))) return …
    // This mirror only had the `ibanOk` half. The blank-name half was reachable only through
    // georgianAccountProblem, which returns null when `banks` is not an array — so wherever the
    // registry has not loaded, a contract with a valid IBAN and NO bank name reads as ready and
    // the server answers 400 invalid_payment_rule. Measured on a live property-first rental: the
    // bar said „მზადაა" and POST /contract returned owner_payment_iban, with owner_payment_bank
    // and owner_payment_swift both empty strings.
    // ⚠️ Deliberately independent of `banks`: a check that only works when a lookup table happens
    //    to be present is the reason this one was missed. The form must be able to say no on its own.
    const bankName = String((stage === 'initial' ? v.payment_bank : v.owner_payment_bank) || '').trim()
    if (needsAccount && !bankName) return stage === 'initial' ? 'payment_bank' : 'owner_payment_bank'
    const compactIban = String(iban || '').replace(/\s/g, '').toUpperCase()
    if (needsAccount && compactIban.startsWith('GE')) {
      const bad = georgianAccountProblem(compactIban, stage === 'initial' ? v.payment_bank : v.owner_payment_bank,
        stage === 'initial' ? v.initial_payment_swift : v.owner_payment_swift, banks)
      if (bad === 'iban') return stage === 'initial' ? 'payment_account' : 'owner_payment_iban'
      if (bad === 'bank') return stage === 'initial' ? 'payment_bank' : 'owner_payment_bank'
      if (bad === 'swift') return stage === 'initial' ? 'initial_payment_swift' : 'owner_payment_swift'
    }
    if ((method === 'international_wire' || ways.includes('international_wire')) && !/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(String(stage === 'initial' ? v.initial_payment_swift : v.owner_payment_swift || '').toUpperCase()))
      return stage === 'initial' ? 'initial_payment_swift' : 'owner_payment_swift'
  }
  if (!['owner', 'authorised_agent', 'agency_settlement_account', 'other'].includes(v.initial_recipient_type)) return 'initial_recipient_type'
  if (v.initial_recipient_type === 'agency_settlement_account' && v.payment_method === 'cash') return 'initial_recipient_type'
  if (['other', 'agency_settlement_account'].includes(v.initial_recipient_type) && !String(v.payment_recipient || '').trim()) return 'initial_recipient_name'
  if (v.initial_recipient_type === 'authorised_agent' && !v.initial_receiving_agent?.party_id) return 'initial_receiving_agent'
  if (v.recurring_recipient_type !== 'owner') return 'recurring_recipient_type'
  return null
}
